import { IFaultDiagnosisHandleItem } from "@/spot_check/api/modules/common";
import { E_AlarmLevel, E_AlarmType } from "../enums";
import { E_YesNo, IOptionNumberItem, IPageData, IPageParams } from "../types";
import { IGetFaultanAlysisStatisticsData } from "./alarm.types";
import { E_ModelPartial } from "./model.types";
import { E_UpkeepRepairHandleStatus } from "@/spot_check/api/modules/types";
import { E_Axis } from "./sensor.types";
import { E_MapType, E_MapTypeStr } from "./diagnosis.types";

/**
 * $t('enum.E_FaultType.RESONANCE_TYPE')
 * $t('enum.E_FaultType.OUTOFFBALANCE_TYPE')
 * $t('enum.E_FaultType.MISALIGNMENT_TYPE')
 * $t('enum.E_FaultType.FOUNDATION_LOOSE_TYPE')
 * $t('enum.E_FaultType.UNEVEN_AIR_GAP_TYPE')
 * $t('enum.E_FaultType.DAMAGED_HOLDER_TYPE')
 * $t('enum.E_FaultType.ROLLING_ELEMENT_DAMAGED_TYPE')
 * $t('enum.E_FaultType.INNER_RING_DAMAGED_TYPE')
 * $t('enum.E_FaultType.OUTER_RING_DAMAGED_TYPE')
 * $t('enum.E_FaultType.POOR_LUBRICATION_TYPE')
 * $t('enum.E_FaultType.EXPERT_ANALYSIS_TYPE')
 * $t('enum.E_FaultType.WATER_PUMP_TYPE')
 * $t('enum.E_FaultType.GEAR_WEAR_TYPE')
 * $t('enum.E_FaultType.GEAR_LOAD_TYPE')
 * $t('enum.E_FaultType.GEAR_ALIGNMENT_TYPE')
 * $t('enum.E_FaultType.FAN_FRICTION_TYPE')
 */
export enum E_FaultType {
    /**
     * 共振
     */
    RESONANCE_TYPE = 1,
    /**
     * 不平衡
     */
    OUTOFFBALANCE_TYPE = 2,
    /**
     * 不对中
     */
    MISALIGNMENT_TYPE = 3,
    /**
     * 松动
     */
    FOUNDATION_LOOSE_TYPE = 4,
    /**
     * 转子气隙不均
     */
    UNEVEN_AIR_GAP_TYPE = 5,
    /**
     * 轴承保持架损坏
     */
    DAMAGED_HOLDER_TYPE = 6,
    /**
     * 轴承滚动体损坏
     */
    ROLLING_ELEMENT_DAMAGED_TYPE = 7,
    /**
     * 轴承内圈损坏
     */
    INNER_RING_DAMAGED_TYPE = 8,
    /**
     * 轴承外圈损坏
     */
    OUTER_RING_DAMAGED_TYPE = 9,
    /**
     * 轴承润滑不良
     */
    POOR_LUBRICATION_TYPE = 10,
    /**
     * 设备存在异常
     */
    EXPERT_ANALYSIS_TYPE = 11,
    /**
     * 水泵叶片摩擦
     */
    WATER_PUMP_TYPE = 12,
    /**
     * 齿轮磨损
     */
    GEAR_WEAR_TYPE = 13,
    /**
     * 齿轮负载高
     */
    GEAR_LOAD_TYPE = 14,
    /**
     * 齿轮不对中
     */
    GEAR_ALIGNMENT_TYPE = 15,
    /**
     * 风机叶片摩擦
     */
    FAN_FRICTION_TYPE = 16,
}

/**
 * $t('enum.E_DiagnosticResults.RESONANCE_RECOMMENDATION')
 * $t('enum.E_DiagnosticResults.OUTOFFBALANCE_RECOMMENDATION')
 * $t('enum.E_DiagnosticResults.MISALIGNMENT_RECOMMENDATION')
 * $t('enum.E_DiagnosticResults.FOUNDATION_LOOSE_RECOMMENDATION')
 * $t('enum.E_DiagnosticResults.UNEVEN_AIR_GAP_RECOMMENDATION')
 * $t('enum.E_DiagnosticResults.DAMAGED_HOLDER_RECOMMENDATION')
 * $t('enum.E_DiagnosticResults.ROLLING_ELEMENT_DAMAGED_RECOMMENDATION')
 * $t('enum.E_DiagnosticResults.INNER_RING_DAMAGED_RECOMMENDATION')
 * $t('enum.E_DiagnosticResults.OUTER_RING_DAMAGED_RECOMMENDATION')
 * $t('enum.E_DiagnosticResults.POOR_LUBRICATION_RECOMMENDATION')
 * $t('enum.E_DiagnosticResults.EXPERT_ANALYSIS_RECOMMENDATION')
 * $t('enum.E_DiagnosticResults.WATER_HOUSING_RECOMMENDATION')
 * $t('enum.E_DiagnosticResults.GEAR_ANALYSIS_RECOMMENDATION')
 * $t('enum.E_DiagnosticResults.GEAR_LOAD_RECOMMENDATION')
 * $t('enum.E_DiagnosticResults.GEAR_ALIGNMENT_RECOMMENDATION')
 * $t('enum.E_DiagnosticResults.GEAR_FRICTION_RECOMMENDATION')
 */
export enum E_DiagnosticResults {
    /**
     * 建议对于变频设备提高或者降低设备的转速，避开共振区间。停机检修时设备基础加固或进行专业模态分析，改变设备整体固有频率。
     */
    RESONANCE_RECOMMENDATION = 11,
    /**
     * 建议检查设备是否有部件脱落，可清理叶轮结垢及检查基础是否松动或薄弱，视运行情况做动平衡校验。
     */
    OUTOFFBALANCE_RECOMMENDATION = 21,
    /**
     * 建议设备停机，重新效验联轴器对中偏差。
     */
    MISALIGNMENT_RECOMMENDATION = 31,
    /**
     * 建议检查轴承、联轴器处是否存在磨损松动，基础螺栓是否松动。
     */
    FOUNDATION_LOOSE_RECOMMENDATION = 41,
    /**
     * 建议设备停机检查设备软脚和联轴器不对中情况，如不能改善更换电机，检查电机的转子和定子偏心问题。
     */
    UNEVEN_AIR_GAP_RECOMMENDATION = 51,
    /**
     * 建议设备停机更换轴承，检查轴承保持架损坏情况。
     */
    DAMAGED_HOLDER_RECOMMENDATION = 61,
    /**
     * 建议设备停机更换轴承，检查轴承滚动体损坏情况。
     */
    ROLLING_ELEMENT_DAMAGED_RECOMMENDATION = 71,
    /**
     * 建议设备停机更换轴承，检查轴承内圈损坏情况。
     */
    INNER_RING_DAMAGED_RECOMMENDATION = 81,
    /**
     * 建议设备停机更换轴承，检查轴承外圈损坏情况。
     */
    OUTER_RING_DAMAGED_RECOMMENDATION = 91,
    /**
     * 建议轴承加油处理。
     */
    POOR_LUBRICATION_RECOMMENDATION = 101,
    /**
     * 建议专家介入，人工分析故障。
     */
    EXPERT_ANALYSIS_RECOMMENDATION = 111,
    /**
     * 建议设备停机时调整水泵叶轮与外壳之间间隙。
     */
    WATER_HOUSING_RECOMMENDATION = 121,
    /**
     * 建议设备停机时检查齿轮磨损情况。
     */
    GEAR_ANALYSIS_RECOMMENDATION = 131,
    /**
     * 建议设备停机时检查齿轮负载情况。
     */
    GEAR_LOAD_RECOMMENDATION = 141,
    /**
     * 建议设备停机时检查齿轮不对中情况。
     */
    GEAR_ALIGNMENT_RECOMMENDATION = 151,
    /**
     * 建议设备停机时调整风机叶片与外壳之间间隙。
     */
    GEAR_FRICTION_RECOMMENDATION = 161,
}


export interface IFaultListPageParams extends IPageParams {
    equipmentId: number
    companyId: number
    areaId: number
    beginTime: string
}

export interface IComponentDTOItem {
    componentId: number
    componentName: string
    /**
     * 测点数组
     */
    mappingDTOList: {
        mappingId: number
        mappingName: string
        sensorId: number
        alarmLevel: E_AlarmLevel[]
        alarmType: E_AlarmType[]
    }[]
}

export interface IFaultListPageItem {
    equipmentId: number
    equipmentName: string
    faultSuggest: string
    /**
     * 部件组数
     */
    componentDTOList: IComponentDTOItem[]
}

export type IFaultListPageData = IPageData<IFaultListPageItem>

/**
 * 参数
 */
export interface IListAlarmParams extends IPageParams {
    companyId: number; // 公司
    areaId: string; // 区域
    equipmentId: string; // 设备
    pointMappingId: string; // 测点
    beginTime: string; // 开始时间
    endTime: string; // 结束时间
    pageNum: number; // 分页
    pageSize: number; // 页码
    levelC: boolean;
    levelD: boolean;
    alarmTypeV: boolean;
    alarmTypeK: boolean;
    alarmTypeP: boolean;
    alarmTypeT: boolean;
    alarmTypeVO: boolean;
    alarmTypeC: boolean;
    alarmTypeOff: boolean;
    dealStatusR: boolean;
    dealStatusX: boolean;
    dealStatusN: boolean;
}
/**
 * 表格列
 */
export interface IListAlarmItem {
    /**
     * 报警等级
     */
    alarmLevel: string;
    /**
     * 报警类型
     */
    alarmType: string;
    areaId: number;
    companyId: number;
    /**
     * 处理人员
     */
    dealMember: string;
    /**
     * 处理状态
     */
    dealStatus: string;
    /**
     * 处理措施
     */
    dealRemark: string;
    /**
     * 设备ID
     */
    equipmentId: number;
    /**
     * 设备名称
     */
    equipmentName: string;
    /**
     * 报警id
     */
    id: number;
    pointId: number;
    pointName: string;
    sensorId: any;
}

/**
 * 表格返回数据
 */
export type IListAlarmData = IPageData<IListAlarmItem>;

export interface IPostUpdateEventParams {
    /**
     * 处理人员
     */
    dealMember: string;
    /**
     * 处理备注
     */
    dealRemark: string;
    /**
     * 是否终止报警
     */
    dealStatus: E_YesNo;
}

/**
 * 故障建议
 */
export interface IFaultSuggestItem {
    faultReason: string;
    faultType: number;
    suggest: string;
}

export type IGetFaultSuggestsData = IFaultSuggestItem[];

export interface IGetHighMappingParams {
    equipmentId: number
    /**
     * example: 2023-04-20
     */
    beginTime: string
}


export interface IDiagnosisListParams extends IPageParams {
    companyId?: number
    areaId?: number
    equipmentId?: number
    /**
     * 故障编号
     */
    faultSn?: string
    /**
     * 故障时间
     */
    startTime: string
    endTime: string
}

export interface IFaultDiagnosisItem {
    alarmId
    alarmLevel
    alarmReason
    alarmType
    createTime
    faultReason
    faultSn
    faultType: E_FaultType
    id
    mappingId
    suggest
    suggestType: E_DiagnosticResults
    waveId: number
}

export interface IDiagnosisListItem {
    areaId: number
    companyId: number
    componentId: number
    componentType: E_ModelPartial
    componentName: number
    equipmentId: number
    equipmentName: number
    faultSn: string
    faultTime
    id
    mappingId
    mappingName
    sensorId
    /**
     * 故障诊断明细表
     */
    faultDiagnosisItemList: IFaultDiagnosisItem[]
    /**
     * 自定义后以这个为准
     */
    suggest: string | null
    suggestId: string // array<E_DiagnosticResults>
}

export type IDiagnosisListData = IPageData<IDiagnosisListItem>

export interface IDiagnosisEquipmentHistoryParams {
    /**
     * 设备ID
     */
    equipmentId: number
    /**
     * 故障编号
     */
    faultSn: string
}

export interface IDiagnosisEquipmentHistoryItem {
    equipmentName: string
    suggestStr: string
    time: string
    suggestJson: Record<any, any>
    suggestTypeId: string // array<E_DiagnosticResults>
}

export type IDiagnosisEquipmentHistoryData = IDiagnosisEquipmentHistoryItem[]

export interface IDiagnosisEquipmentGatherParams {
    /**
     * 设备ID
     */
    equipmentId: number
    /**
     * 故障编号
     */
    faultSn: string
    /**
    * 维保建议
    */
    suggest: string
}

export interface IDiagnosisWaveIdItem {
    mappingId: number
    mappingName: string
    waveId: number
}

export type IDiagnosisGetWaveIdItem = IDiagnosisWaveIdItem[]

export interface IFaultSetting {
    /**
     * FFT点数
     */
    fftPoints: number
    /**
     * 倍频系数
     */
    freqFactor: number
    k1: number
    k2: number
    k3: number
    k4: number
    k5: number
    k6: number
    k7: number

    /**
     * 齿轮箱配置
     */
    k11: number
    k12: number
    k13: number
    k14: number
    k15: number
    k16: number

    /**
     * 分辨率等级
     */
    resolutionLevel: number
    /**
     * 采样频率
     */
    sampleRate: number
    /**
     * 传感器id
     */
    sensorId: string
    /**
     * 传感器类型
     */
    sensorType: string
}

export interface IEditFaultSettingParams {
    fftPoints: number
    freqFactor: number
    k1: number
    k2: number
    k3: number
    k4: number
    k5: number
    k6: number
    k7: number

    /**
     * 齿轮箱配置
     */
    k11: number
    k12: number
    k13: number
    k14: number
    k15: number
    k16: number

    resolutionLevel: number
    sampleRate: number
    sensorId: string
    sensorType: string
}

export interface ICopyFaultSettingBySensorIdParams {
    oldSensorId: string
    sensorIds: string[]
}


export interface IStatisticsFaultEditSuggestParams {
    equipmentId: number
    equipmentName: string
    id: number
    /**
     * 故障描述
     */
    suggest: string
}

export type IStatisticsFaultListParams = IPageParams & Partial<{
    areaId: number
    companyId: number
    equipmentId: number
    startTime: string
    endTime: string
}>

export interface IStatisticsFaultListItem {
    /**
     * 报警类型
     */
    alarmReason: string
    alarmTypeId: string // array<E_AlarmType>

    areaName: string
    companyName: string
    equipmentId: number
    equipmentName: string
    faultReason: string
    faultTypeId: string // array<E_FaultType>
    id: number
    statisticsTime: string
    suggest: string
    suggestTypeId: string // array<E_DiagnosticResults>
    /**
     * 平台处理
     */
    diagnosisHandleId?: number
    /**
     * 维修单
     */
    repairId?: number
    repairStatus?: E_UpkeepRepairHandleStatus
    /**
     * 保养单
     */
    upkeepId?: number
    upkeepStatus?: E_UpkeepRepairHandleStatus
}
export type IStatisticsFaultListData = IPageData<IStatisticsFaultListItem>

export interface IStatisticsHistoryItem {
    createTime: string
    updateTime: string
    equipmentId: string
    equipmentName: string
    suggest: string
    statisticsId: number
    suggestTypeId: string
}
export type IStatisticsFaultGetHistoryData = IStatisticsHistoryItem[]

export interface IStatisticsFaultGetTodayStatisticsData {
    /**
     * 报警类型
     */
    alarmReason: string
    equipmentName: string
    /**
     * 故障类型
     */
    faultReason: string
    statisticsTime: string
    suggest: string
}

export interface IFaultSettingEx extends IFaultSetting {
    configId: number
    waveId: number
    mappingId: number
    ratedSpeed: number
    diagnosisModelCodeList: E_MapTypeStr[]
}

export interface IStatisticsFaultThresholdCheckParams {
    sensorId: string
    mappingId: number
    sensorType?: string
}

export type IStatisticsFaultThresholdCheckData = IFaultSettingEx
export type IStatisticsFaultWaveDiagnosisParams = IFaultSettingEx & Partial<{
    alarmAxis: string
    inspectTaskId: number
    extendId: number
    debugStatus?: number
    mapTypes: E_MapTypeStr[]
}>
export type IStatisticsFaultWaveDiagnosisData = IGetFaultanAlysisStatisticsData & {
    suggest: string
}

export interface IStatisticsFaultCheckPasswordParams {
    checkPassword: string
}

export interface IStatisticsFaultGetExtendParams {
    equipmentId: number
}

export type IStatisticsFaultGetExtendData = IOptionNumberItem[]


export type IStatisticsFaultInspectionWaveDiagnosisParams =
    IStatisticsFaultWaveDiagnosisParams
export type IStatisticsFaultInspectionWaveDiagnosisData =
    IStatisticsFaultWaveDiagnosisData


export interface IStatisticsFaultWaveUmDiagnosisParams {
    alarmAxis: E_Axis
    /**
     * 算法配置Id
     */
    mapTypeList: string[]
    /**
     * 测点Id
     */
    mappingId: number
    /**
     * 转速
     */
    ratedSpeed: number
    /**
     * 波形Id
     */
    waveId: number
}

export type IStatisticsFaultWaveUmDiagnosisData = number

export interface IFaultDiagnosiisHandleSaveParams {
    /**
     * 故障诊断统计表ID
     */
    diagnosisStatisticsId: number
    /**
     * 处理措施
     */
    handlingMethod: number
    /**
     * 处理人员
     */
    handlingPerson: number
    /**
     * 处理图片
     */
    img1FileId: number
    /**
     * 
     */
    img2FileId: number
    /**
     * 
     */
    img3FileId: number
}

export interface IFaultDiagnosisHandleDetailData {
    /**
     * 重新诊断结果
     */
    faultDiagnosisHandleList: IFaultDiagnosisHandleItem[]
    /**
     * 故障诊断统计表ID
     */
    diagnosisStatisticsId: number
    /**
     * 处理措施
     */
    handlingMethod: string
    /**
     * 处理人员
     */
    handlingPerson: string
    /**
     * 处理图片
     */
    img1FileId: number
    img1FileUrl: string
    /**
     * 
     */
    img2FileId: number
    img2FileUrl: string
    /**
     * 
     */
    img3FileId: number
    img3FileUrl: string
}